import { Avatar } from '@roger/ui-components';
import { HeartPulse, Megaphone, Pencil, Users } from 'lucide-react';
import { NEW_MESSAGE_WINDOW } from '../../store/slices/chatWindows';
import { useChannel, useInbox } from '../../hooks/chat/useChatQueries';
import { initialsOf } from './initials';
import { isDirectChannel } from './windowHeader';

/**
 * The round icon of a window or tab: initials for a direct message, the people
 * icon for a care team or group, a megaphone for an announcement channel.
 */
export function WindowBadge({
  id,
  size = 'md',
}: {
  id: string;
  size?: 'sm' | 'md';
}) {
  const isNew = id === NEW_MESSAGE_WINDOW;
  const { data: inbox } = useInbox();
  const row = inbox?.find(r => r.channel_id === id);
  // The inbox row is the cheaper source; the header is read only without one.
  const { data: header } = useChannel(isNew || row ? null : id);

  const title = row?.title ?? header?.title ?? '';
  const kind = row?.kind ?? header?.kind;
  const patient = row?.patient ?? header?.patient_card;
  const isDirect = isDirectChannel(row, header);
  const disc = size === 'sm' ? 'h-6 w-6' : 'h-8 w-8';
  const icon = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  if (isNew) {
    return (
      <span
        className={`flex ${disc} shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600`}
      >
        <Pencil className={icon} />
      </span>
    );
  }
  if (kind === 'announcement') {
    return (
      <span
        className={`flex ${disc} shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600`}
      >
        <Megaphone className={icon} />
      </span>
    );
  }
  if (isDirect) {
    return (
      <Avatar
        size='sm'
        initials={initialsOf(title)}
        className={`shrink-0 ${disc}`}
      />
    );
  }
  return (
    <span
      className={`flex ${disc} shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600`}
    >
      {patient ? <HeartPulse className={icon} /> : <Users className={icon} />}
    </span>
  );
}
